/**
 * IndexedDB Storage for Model Data using Dexie
 *
 * Provides persistent storage accessible from both main thread and web workers.
 * Used for ScenarioNodes, ScenarioNodeMaterials, ScenarioNodeSupplementals.
 *
 * Database structure:
 * - Store: "modelData"
 *   - Key: "{dataType}:{modelId}:{scenarioId}"
 *   - Value: { data, timestamp, modelId, scenarioId, dataType }
 *
 * Storage format:
 * - scenarioNodes: NodeDto[] (array)
 * - scenarioNodeMaterials: [nodeId, NodeMaterialDto][] (Map entries for O(1) lookup)
 * - scenarioNodeSupplementals: [nodeId, NodeSupplementalDto][] (Map entries for O(1) lookup)
 */
import type { INodeMaterialDto, INodeSupplementalDto } from '@api/apiClient';
import Dexie, { Table } from 'dexie';

// Cache expiry: 24 hours (data rarely changes)
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;

export type ModelDataType =
  | 'scenarioNodes'
  | 'scenarioNodeMaterials'
  | 'scenarioNodeSupplementals';

export interface CachedModelData<T = unknown> {
  key: string;
  dataType: ModelDataType;
  modelId: string;
  scenarioId?: string;
  data: T;
  timestamp: number;
  version?: string;
}

// ============================================================================
// Database Definition
// ============================================================================

class ModelDataDB extends Dexie {
  modelData!: Table<CachedModelData>;

  constructor() {
    super('model-data-cache');
    this.version(1).stores({
      modelData: 'key, dataType, modelId, timestamp',
    });
  }
}

// Export singleton instance - works in both main thread and workers
export const db = new ModelDataDB();

// ============================================================================
// Helpers
// ============================================================================

/**
 * Generates a cache key for model data.
 */
export function getCacheKey(
  dataType: ModelDataType,
  modelId: string,
  scenarioId?: string
): string {
  return scenarioId ? `${dataType}:${modelId}:${scenarioId}` : `${dataType}:${modelId}`;
}

// ============================================================================
// Map Conversion Utilities
// ============================================================================

/**
 * Type alias for serialized Map entries stored in IndexedDB.
 * Maps are stored as arrays of [key, value] tuples to preserve number keys.
 */
export type SerializedMap<K, V> = [K, V][];

/**
 * Converts a NodeMaterialDto array to a Map keyed by nodeId.
 * Used when writing to IndexedDB for O(1) lookup on read.
 * Generic type T allows both INodeMaterialDto and NodeMaterialDto.
 */
export function materialsArrayToMap<T extends INodeMaterialDto>(
  materials: T[]
): Map<number, T> {
  const map = new Map<number, T>();
  for (const material of materials) {
    if (material.nodeId != null) {
      map.set(material.nodeId, material);
    }
  }
  return map;
}

/**
 * Converts a NodeSupplementalDto array to a Map keyed by nodeId.
 * Used when writing to IndexedDB for O(1) lookup on read.
 * Generic type T allows both INodeSupplementalDto and NodeSupplementalDto.
 */
export function supplementalsArrayToMap<T extends INodeSupplementalDto>(
  supplementals: T[]
): Map<number, T> {
  const map = new Map<number, T>();
  for (const supp of supplementals) {
    if (supp.nodeId != null) {
      map.set(supp.nodeId, supp);
    }
  }
  return map;
}

/**
 * Serializes a Map to an array of tuples for IndexedDB storage.
 * Preserves key types (important for number keys like nodeId).
 */
export function serializeMap<K, V>(map: Map<K, V>): SerializedMap<K, V> {
  return [...map.entries()];
}

/**
 * Deserializes an array of tuples back to a Map.
 */
export function deserializeMap<K, V>(entries: SerializedMap<K, V>): Map<K, V> {
  return new Map(entries);
}

// ============================================================================
// CRUD Operations
// ============================================================================

/**
 * Reads model data from IndexedDB.
 * Returns null if not found or expired.
 */
export async function readModelData<T>(
  dataType: ModelDataType,
  modelId: string,
  scenarioId?: string,
  ttlMs = DEFAULT_TTL_MS
): Promise<T | null> {
  try {
    const key = getCacheKey(dataType, modelId, scenarioId);
    const cached = await db.modelData.get(key);

    if (!cached || Date.now() - cached.timestamp > ttlMs) {
      return null;
    }

    return cached.data as T;
  } catch (error) {
    console.warn('[modelDataStorage] Error reading from IDB:', error);
    return null;
  }
}

/**
 * Writes model data to IndexedDB.
 */
export async function writeModelData<T>(
  dataType: ModelDataType,
  modelId: string,
  scenarioId: string | undefined,
  data: T,
  version?: string
): Promise<void> {
  const dataLength = Array.isArray(data) ? data.length : 'not-array';
  console.log('[modelDataStorage] writeModelData:', { dataType, modelId, scenarioId, dataLength, version });

  try {
    const key = getCacheKey(dataType, modelId, scenarioId);

    await db.modelData.put({
      key,
      dataType,
      modelId,
      scenarioId: scenarioId,
      data,
      timestamp: Date.now(),
      version,
    });

    // Verify write succeeded
    const verification = await db.modelData.get(key);
    const verifyLength = verification && Array.isArray(verification.data) ? verification.data.length : 'not-array';
    console.log('[modelDataStorage] writeModelData verified:', { key, verifyLength, timestamp: verification?.timestamp });
  } catch (error) {
    console.warn('[modelDataStorage] Error writing to IDB:', error);
  }
}

/**
 * Deletes model data from IndexedDB.
 */
export async function deleteModelData(
  dataType: ModelDataType,
  modelId: string,
  scenarioId?: string
): Promise<void> {
  try {
    const key = getCacheKey(dataType, modelId, scenarioId);
    await db.modelData.delete(key);
  } catch (error) {
    console.warn('[modelDataStorage] Error deleting from IDB:', error);
  }
}

// ============================================================================
// Batch Operations
// ============================================================================

/**
 * Reads multiple model data entries at once.
 * Returns a map of modelId -> data.
 */
export async function readMultipleModelData<T>(
  dataType: ModelDataType,
  modelIds: string[],
  scenarioId?: string,
  ttlMs = DEFAULT_TTL_MS
): Promise<Map<string, T>> {
  const result = new Map<string, T>();
  if (modelIds.length === 0) return result;

  try {
    const keys = modelIds.map((id) => getCacheKey(dataType, id, scenarioId));
    const cached = await db.modelData.bulkGet(keys);
    const now = Date.now();

    cached.forEach((item, i) => {
      if (item && now - item.timestamp <= ttlMs) {
        result.set(modelIds[i], item.data as T);
      }
    });

    return result;
  } catch (error) {
    console.warn('[modelDataStorage] Error reading multiple from IDB:', error);
    return result;
  }
}

/**
 * Writes multiple model data entries at once.
 */
export async function writeMultipleModelData<T>(
  dataType: ModelDataType,
  entries: Array<{ modelId: string; scenarioId?: string; data: T; }>,
  version?: string
): Promise<void> {
  if (entries.length === 0) return;

  try {
    const now = Date.now();
    const records: CachedModelData<T>[] = entries.map((entry) => ({
      key: getCacheKey(dataType, entry.modelId, entry.scenarioId),
      dataType,
      modelId: entry.modelId,
      scenarioId: entry.scenarioId,
      data: entry.data,
      timestamp: now,
      version,
    }));

    await db.modelData.bulkPut(records);
  } catch (error) {
    console.warn('[modelDataStorage] Error writing multiple to IDB:', error);
  }
}

// ============================================================================
// Version/ETag Operations
// ============================================================================

/**
 * Reads model data with version info for conditional fetching.
 * Returns data, version (etag), and staleness status.
 */
export async function readModelDataWithVersion<T>(
  dataType: ModelDataType,
  modelId: string,
  scenarioId?: string,
  ttlMs = DEFAULT_TTL_MS
): Promise<{ data: T | null; version: string | null; isStale: boolean; }> {
  try {
    const key = getCacheKey(dataType, modelId, scenarioId);
    const cached = await db.modelData.get(key);

    if (!cached) {
      return { data: null, version: null, isStale: true };
    }

    const isStale = Date.now() - cached.timestamp > ttlMs;
    return {
      data: cached.data as T,
      version: cached.version ?? null,
      isStale,
    };
  } catch (error) {
    console.warn('[modelDataStorage] Error reading with version:', error);
    return { data: null, version: null, isStale: true };
  }
}

/**
 * Reads cached versions (etags) for multiple models.
 * Used for conditional batch fetching - client sends these to server.
 * @returns Map of modelId -> version (etag), null if not cached
 */
export async function readModelVersions(
  dataType: ModelDataType,
  modelIds: string[],
  scenarioId?: string
): Promise<Map<string, string | null>> {
  const result = new Map<string, string | null>();
  if (modelIds.length === 0) return result;

  try {
    const keys = modelIds.map((id) => getCacheKey(dataType, id, scenarioId));
    const cached = await db.modelData.bulkGet(keys);

    cached.forEach((item, i) => {
      result.set(modelIds[i], item?.version ?? null);
    });

    return result;
  } catch (error) {
    console.warn('[modelDataStorage] Error reading versions:', error);
    // Return empty versions - will trigger full fetch
    modelIds.forEach(id => result.set(id, null));
    return result;
  }
}

/**
 * Converts a Map of versions to a plain object for API requests.
 * Filters out null values (models without cached etags).
 */
export function versionsMapToObject(versions: Map<string, string | null>): Record<string, string> {
  const result: Record<string, string> = {};
  versions.forEach((version, modelId) => {
    if (version !== null) {
      result[modelId] = version;
    }
  });
  return result;
}

// ============================================================================
// Maintenance
// ============================================================================

/**
 * Clears all data for a specific type.
 */
export async function clearDataByType(dataType: ModelDataType): Promise<number> {
  try {
    return await db.modelData.where('dataType').equals(dataType).delete();
  } catch (error) {
    console.warn('[modelDataStorage] Error clearing data by type:', error);
    return 0;
  }
}

/**
 * Clears expired data from the cache.
 */
export async function clearExpiredData(ttlMs = DEFAULT_TTL_MS): Promise<number> {
  try {
    const cutoff = Date.now() - ttlMs;
    return await db.modelData.where('timestamp').below(cutoff).delete();
  } catch (error) {
    console.warn('[modelDataStorage] Error clearing expired data:', error);
    return 0;
  }
}

/**
 * Clears all cached model data.
 */
export async function clearAllData(): Promise<void> {
  try {
    await db.modelData.clear();
  } catch (error) {
    console.warn('[modelDataStorage] Error clearing all data:', error);
  }
}

/**
 * Gets storage statistics.
 */
export async function getStorageStats(): Promise<{
  totalEntries: number;
  byType: Record<ModelDataType, number>;
}> {
  try {
    const all = await db.modelData.toArray();
    const byType: Record<string, number> = {};

    for (const item of all) {
      byType[item.dataType] = (byType[item.dataType] || 0) + 1;
    }

    return {
      totalEntries: all.length,
      byType: byType as Record<ModelDataType, number>,
    };
  } catch (error) {
    console.warn('[modelDataStorage] Error getting stats:', error);
    return { totalEntries: 0, byType: {} as Record<ModelDataType, number> };
  }
}

/**
 * Invalidates (deletes) cached data for a specific model.
 * Useful when model data has been updated on the server.
 */
export async function invalidateModelCache(
  modelId: string,
  scenarioId?: string
): Promise<number> {
  try {
    // If scenarioId provided, only invalidate that specific combination
    if (scenarioId) {
      const dataTypes: ModelDataType[] = [
        'scenarioNodes',
        'scenarioNodeMaterials',
        'scenarioNodeSupplementals',
      ];
      let count = 0;
      for (const dataType of dataTypes) {
        const key = getCacheKey(dataType, modelId, scenarioId);
        await db.modelData.delete(key);
        count++;
      }
      return count;
    }

    // Otherwise invalidate all data for this model
    return await db.modelData.where('modelId').equals(modelId).delete();
  } catch (error) {
    console.warn('[modelDataStorage] Error invalidating cache:', error);
    return 0;
  }
}

